import { getGroups } from "../services/keycloak";
import { ROLES, UserRole } from "../utils/hooks/useUserRole";

/**
 * Field roles operate exclusively through the mobile PWA and must never reach
 * GDP (the web platform). A user whose ONLY role(s) are field roles is blocked
 * at the entrance with the Permission Denied page (e.g. the Sale role).
 */
const FIELD_ONLY_ROLES: string[] = [
  ROLES.BRANCH_SALES_PERSON,
  ROLES.BRANCH_CHECKER,
];

const normalizeGroup = (group: string) => group.trim().toLowerCase();

/**
 * Who maintains a shop's details on the customer page: every back-office role the server's
 * POST / PATCH /customers admits, except marketing. Marketing owns pricebooks rather than
 * shops, so on its own it may only move a shop between pricebooks; holding another of these
 * roles as well keeps that role's reach.
 */
const isCustomerEditor = (role: UserRole): boolean =>
  Boolean(
    role.isAdmin ||
      role.isMaster ||
      role.isHeadOfficeAccounting ||
      role.isBranchManager ||
      role.isBranchAccounting ||
      role.isBranchFinance ||
      role.isBranchFinanceAndAccount,
  );

/**
 * Who follows the catalogue from head office: the catalogue writers (admin, master, marketing)
 * and head-office accounting, which reads without the edits.
 */
const isCatalogueReviewer = (role: UserRole): boolean =>
  Boolean(
    role.isAdmin ||
      role.isMaster ||
      role.isMarketing ||
      role.isHeadOfficeAccounting,
  );

/**
 * The Express back office: the server's EXPRESS_BACK_OFFICE_ROLES — the branch desk (manager,
 * accounting, finance, finance-and-accounting), head-office accounting and admin; master passes
 * every server rbac check. The field roles hold a branch group too but are refused there.
 */
const isExpressBackOffice = (role: UserRole): boolean =>
  Boolean(
    role.isBranchManager ||
      role.isBranchAccounting ||
      role.isBranchFinance ||
      role.isBranchFinanceAndAccount ||
      role.isHeadOfficeAccounting ||
      role.isAdmin ||
      role.isMaster,
  );

/** Head office on the Express screens: head-office accounting, admin and master. */
const isExpressHeadOffice = (role: UserRole): boolean =>
  Boolean(role.isHeadOfficeAccounting || role.isAdmin || role.isMaster);

/**
 * App-level gate. Returns `false` when the current user must not enter GDP at
 * all (e.g. a Sale-only user). Used to replace the whole app content with the
 * Permission Denied page.
 */
export function canAccessGDP(
  groups: string[] | undefined = getGroups(),
): boolean {
  const normalized = (groups ?? []).map(normalizeGroup);

  // Users with no resolved role fall through to the per-feature guards / API.
  if (normalized.length === 0) return true;

  // Allowed in only if they hold at least one non-field (desktop) role. The BDC roles
  // (bdc manager / bdc operator) are desktop roles: they enter, confined to BDC.
  return normalized.some((group) => !FIELD_ONLY_ROLES.includes(group));
}

/**
 * Per-feature permission rules. Single source of truth shared by the route
 * guards (direct URL access) and the navbar (menu visibility) so that a hidden
 * menu item and a typed-in URL always resolve to the same decision.
 *
 * Head-office accounting is TWO Keycloak groups — the accountant and the
 * supervisor above them — and the supervisor holds everything the accountant
 * holds. Every rule below therefore asks `isHeadOfficeAccounting`, which is true
 * for either grade, and every comment naming "head office" or "head-office
 * accounting" means the pair. A rule may only ask `isHoAccountingSupervisor` for
 * a power that is the supervisor's ALONE.
 *
 * The rules that mirror a server-side cohort hold on the same terms: the server
 * decides, and its own role list has to carry the supervisor grade too, or the
 * screen opens onto a call that 403s.
 */
export const featureAccess = {
  userManagement: (role: UserRole) => Boolean(role.isAdmin || role.isMaster),
  // ตั้งค่าระบบ — company-wide settings and master data (the seller printed on every
  // document, chain export layouts, policy values). The server lets MASTER alone write
  // behind every tab and read the settings history, so the page is MASTER's alone too.
  systemSettings: (role: UserRole) => Boolean(role.isMaster),
  salesReportExport: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting,
    ),
  // ยอดขายทั้งบริษัท (HO) — GET /reports/company-sales: master and head-office accounting (both
  // grades), as on the server. Company-wide, so no branch role reaches it.
  companySalesReport: (role: UserRole) =>
    Boolean(role.isMaster || role.isHeadOfficeAccounting),
  gb1SaleReport: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting,
    ),
  eTax: (role: UserRole) => Boolean(role.isHeadOfficeAccounting),
  // QR Transaction (รายการ QR) — the only screen that reaches a QR payment with no usable
  // bill behind it. Mirrors the server's own view cohort, ADMIN included: ADMIN reads every
  // branch here and can settle nothing, so the screen is safe to show it.
  qrTransaction: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // QR Dashboard (แดชบอร์ด QR) — the aggregate read of the same QR story the รายการ QR
  // screen shows row by row, so it carries that screen's cohort exactly. Nothing on it can
  // be acted upon; it only counts bills that already exist.
  qrAnalytics: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // สรุปรอบเข้าเยี่ยมร้าน — served by the dashboard controller, so it carries that
  // controller's cohort (HO accounting, branch manager/accounting/finance/finance-and-
  // accounting, admin; master passes every server rbac check). Read-only.
  visitFrequency: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Which altitude สรุปรอบเข้าเยี่ยมร้าน opens at. Mirrors the server's getQueryUserEntity:
  // only HO accounting and master may name a branch — ADMIN is pinned to its own branch
  // there, so it opens on its branch like any branch reader.
  visitFrequencyAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // สรุปถาดขึ้น–ถาดกลับ — served under the same rbac cohort and branch pinning as
  // สรุปรอบเข้าเยี่ยมร้าน, so both keys carry visitFrequency's sets exactly. Read-only.
  palletSummary: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  palletSummaryAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // หมายเหตุ on a สรุปถาด trip — the branch explains its own tray differences, so the branch
  // back office alone writes it; head office, admin and master read it. Mirrors the server's
  // cohort on PUT /pallet-summary/trips/:checkin_uuid/note.
  palletNoteWrite: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount,
    ),
  // สรุปเปอร์เซ็นต์ของขึ้น ของเสีย — served under สรุปถาด's rbac cohort and branch pinning, so
  // both keys carry palletSummary's sets exactly. Read-only.
  wastePercentReport: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  wastePercentReportAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // Sale Deposit Report (รายงานการโอน) — HO sees every branch; the BO review
  // cohort sees only its JWT-pinned branch. Admin/master retain oversight.
  saleDepositReport: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Which altitude รายงานการโอน opens at. A cross-branch reader gets one row per branch-day
  // and drills into a branch; a branch-scoped reader keeps the route-level table, because a
  // summary of their own branch would be a single row and would hide the routes they are
  // there to tick off. Not a server cohort — every one of these roles may already read the
  // report; this only decides what the page renders first.
  saleDepositBranchSummary: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isAdmin || role.isMaster),
  // Sale-deposit transfer columns in the BO Delivery Information table are scoped
  // to branch finance/accounting/manager. HO continues to review transfers on its
  // dedicated Sale Deposit Report page; admin/master keep superuser visibility.
  saleDepositColumns: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Correcting a slip amount against its image is a finance/accounting judgement about
  // money received, so it is scoped tighter than the review columns: the branch manager
  // may tick ตรวจแล้ว but does not re-key the figures. The server enforces the same set.
  saleDepositEditAmount: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Re-filing the expense figures (ค่าน้ำมัน, ค่าธรรมเนียมโอน, ฝากเก็บ …) after the
  // salesperson has already uploaded slips. Deliberately NOT saleDepositEditAmount's cohort:
  // that one corrects a slip against its IMAGE, evidence anyone reviewing can read, so head
  // office holds it. These figures are the branch's own declaration of what it spent, filed
  // on แจ้งยอดโอน — head office reviews that declaration rather than making it, and is absent
  // here; the branch manager, who authorised the money leaving, is present.
  // Mirrors TRANSFER_NOTIFICATION_ROLES on the server's PATCH /sale-deposits/:uuid/expenses.
  saleDepositEditExpenses: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isBranchManager ||
        role.isAdmin ||
        role.isMaster,
    ),
  // ตรวจแล้ว is two sign-offs, and the server picks the side from the token — these two
  // decide which checkbox the screen lets a user reach, and must agree with it: the
  // cross-branch cohort owns the HO column, every other reviewer owns the BO column.
  saleDepositReviewAsHo: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isAdmin || role.isMaster),
  saleDepositReviewAsBo: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount,
    ),
  // ปิดศูนย์เซล — keying in the cash head office received against Express and freezing the
  // branch-day, plus handing it back to the branch. The ONE rule in this map that asks
  // `isHoAccountingSupervisor` rather than `isHeadOfficeAccounting`: closing is the single
  // power separating the two head-office grades. The plain accountant checks the same
  // branch-day and ticks ตรวจแล้ว (HO) on it, but may not decide the day is finished.
  // Admin/master act as the supervisor, as everywhere else in this map. Mirrors the server's
  // cohort on POST /sale-center-closings and /sale-center-closings/send-back — everyone else
  // in the review cohort still READS the ปิดศูนย์เซล panel and the figures a closed day was
  // signed on, but is not shown the form that closes it.
  saleCenterClosing: (role: UserRole) =>
    Boolean(role.isHoAccountingSupervisor || role.isAdmin || role.isMaster),
  // คำขอแก้ไขยอดโอน — asking the supervisor to reopen ONE deposit after the branch-day was
  // closed. The branch back office that FILES the money: the same cohort that re-keys the
  // expense figures, because a request is that correction asked for rather than made. Head
  // office is deliberately absent — it answers these requests, it does not raise them, and
  // giving it both ends would let one grade approve its own. Admin/master keep superuser
  // reach, as everywhere else in this map. Mirrors the server's cohort on
  // POST /sale-deposit-edit-requests; deciding the request is `saleCenterClosing`'s cohort,
  // the same exclusive grade that closed the day in the first place.
  saleDepositRequestEdit: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isBranchManager ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Attaching the slips a salesperson never sent is branch bookkeeping on the branch's own
  // deposit, so it is the finance/accounting cohort only — the same set that may re-key a
  // slip amount, minus head office, which reviews the branch rather than files for it.
  saleDepositUploadSlipOnBehalf: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount,
    ),
  // Reprint Authorization is an approver governance screen. Field roles (SALE /
  // checker) and plain branch roles must NOT see the page or the create/revoke
  // actions — only the accounting/finance/manager approvers (+ admin/master).
  reprintAuth: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Reprint Requests (GDP-1398 / GDP-1401) — the approver inbox for reprint
  // requests raised by salespeople in the PWA. Mirrors reprintAuth's cohort, and
  // matches the BE's `approverRoles` on the reprint-request controller (admin,
  // master, branch manager, branch finance, branch accounting, branch finance
  // and accounting, head-office accounting). Salespeople review nothing: they
  // raise requests from the PWA and are field-only, so they never reach GDP.
  reprintRequest: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Per-customer bill-footer discount: admin, master and head-office accounting — everyone
  // reads the value, few may write it. Mirrors CUSTOMER_DISCOUNT_ROLES on the server's
  // PATCH /customers/:id/discount, which kept this cohort when the catalogue moved to marketing.
  customerDiscount: (role: UserRole) =>
    Boolean(role.isAdmin || role.isHeadOfficeAccounting || role.isMaster),
  // Switching a shop between กำลังใช้งาน and ไม่เปิดใช้งาน on the customer list. Was the
  // branch manager alone, so head office and the back office had to ask the GM to press
  // the button for them. Same approver cohort as printSettings / reprintAuth, and matches
  // the BE's STATUS_TOGGLE_ROLES on PATCH /customers/:id/status exactly.
  customerStatusEdit: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Branch picker on the customer list. Head office has always had it; ADMIN is here
  // because it sets the bill discount on shops in branches it does not belong to, and
  // without a branch to pick, the list only ever answers with ADMIN's own. Marketing binds
  // the shops of every branch to its pricebooks, so it reads every branch too. Mirrors the
  // server's cross-branch scope on GET /customers and GET /routes.
  customerBranchScope: (role: UserRole) =>
    Boolean(
      role.isAdmin ||
        role.isHeadOfficeAccounting ||
        role.isMaster ||
        role.isMarketing,
    ),
  // Creating a shop and editing it in the customer drawer.
  customerEdit: isCustomerEditor,
  // Moving a shop onto another pricebook (PriceBookModal, PATCH /customers/:id with
  // `pricebook` alone): every shop editor, plus marketing, whose PATCH the server limits to
  // the pricebook binding.
  customerPricebookBind: (role: UserRole) =>
    Boolean(role.isMarketing) || isCustomerEditor(role),
  // Per-customer print settings (GDP-1357). Same approver cohort as reprintAuth:
  // only accounting/finance/manager approvers (+ admin/master) may configure how
  // a customer's bills are printed. Plain branch/field roles must not see it.
  printSettings: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Correcting a bill's payment channel / payment status on the Sales table. The owner
  // named three groups — การเงิน (finance), บัญชี (accounting), สาขา (branch) — with
  // IDENTICAL rights; there are deliberately no per-role differences here.
  // `isBranchFinanceAndAccount` is included because that role IS both of the first two.
  // Admin/master keep superuser reach, as everywhere else in this map.
  // Matches the BE's `PAYMENT_EDIT_ROLES` on the transaction controller exactly.
  // Branch users are already confined to their own branch by the company_id scoping the
  // API applies; no second branch check is added on the client.
  transactionPaymentEdit: (role: UserRole) =>
    Boolean(
      role.isBranchFinance ||
        role.isBranchAccounting ||
        role.isBranchFinanceAndAccount ||
        role.isBranchManager ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // ข้อมูล Express — the bills a branch keys into Express. Mirrors EXPRESS_BACK_OFFICE_ROLES on
  // the server's /express-data: the branch back office (manager, accounting, finance,
  // finance-and-accounting), head-office accounting and admin; master passes every check. The
  // field roles hold a branch group too but are refused there, so `isBranch` is not enough.
  expressData: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Which of them pick the branch on ข้อมูล Express. Mirrors the server's getQueryUserEntity:
  // only head office and master may name a branch — ADMIN is pinned to its own branch there,
  // so it opens on its branch like any branch reader.
  expressDataAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // ใบลดหนี้ / ใบเพิ่มหนี้ — keyed, printed and cancelled by the branch back office for its own
  // branch, and by head-office accounting, admin and master. The same cohort as expressData:
  // the server's /credit-debit-notes carries EXPRESS_BACK_OFFICE_ROLES too.
  creditDebitNote: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Who pulls the notes into Excel (the list's Export Excel). Mirrors CREDIT_DEBIT_NOTE_EXPORT_ROLES
  // on GET /credit-debit-notes/export — head-office accounting and admin; master passes the rbac.
  creditDebitNoteExport: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isAdmin || role.isMaster),
  // Which of them pick the branch a note belongs to (list filter and the create form). Mirrors
  // the server's isCrossBranchRole on /credit-debit-notes — head office, admin and master name
  // the branch (required on create); everyone else is pinned to the branch in their token.
  // Wider than expressDataAllBranches: /express-data still pins ADMIN (getQueryUserEntity).
  creditDebitNoteAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isAdmin || role.isMaster),
  // ซื้อเชื่อ, ซื้อสด, รายได้อื่นๆ and รับชำระหนี้ — keyed, edited and cancelled by the Express back
  // office, a branch for its own branch: /express-documents carries EXPRESS_BACK_OFFICE_ROLES.
  expressDocument: isExpressBackOffice,
  // Which of them name the branch a document belongs to (list filter and the create form), as the
  // server's isCrossBranchRole decides on /express-documents, the same as on /credit-debit-notes.
  expressDocumentAllBranches: isExpressHeadOffice,
  // The Express ผู้จำหน่าย / ลูกค้า and รหัสบัญชี lists — head office keeps both lists: the server
  // lets head-office accounting (either grade) and admin create and edit them, master through the
  // rbac bypass. The branches read them only through the document form's pickers.
  expressMaster: isExpressHeadOffice,
  // ค่าใช้จ่ายรายวัน (รับจ่าย S2) — the branch back office keys its own branch's daily expenses and
  // hands them to head office. Mirrors DAILY_EXPENSE_BRANCH_ROLES on the server's GET
  // /daily-expenses and every write on it (branch accounting, branch finance and accounting).
  // MASTER passes the server's rbac but is head office there — it would have to name a branch,
  // and the writes refuse it — so this branch desk is not offered to it.
  dailyExpense: (role: UserRole) =>
    Boolean(role.isBranchAccounting || role.isBranchFinanceAndAccount),
  // อนุมัติค่าใช้จ่าย (รับจ่าย S3) — approving, sending back and bulk-approving every branch's daily
  // expenses. Mirrors DAILY_EXPENSE_REVIEW_ROLES (head-office accounting, either grade); master
  // passes every server rbac check and is treated as head office there.
  dailyExpenseReview: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // ปิดเดือน / ประมาณการ (รับจ่าย S4) — closing month M and setting the M+1 estimate of every
  // branch. Mirrors MONTHLY_EXPENSE_CLOSE_ROLES (head-office accounting, either grade); master
  // passes every server rbac check and may save and close there too.
  monthlyExpenseClose: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // ประมาณการเดือนนี้ (รับจ่าย S5) — read-only: a branch sees its own estimate, head office any
  // branch's. Mirrors MONTHLY_EXPENSE_ESTIMATE_ROLES (branch accounting, branch manager, head
  // office accounting); master passes the server's rbac as head office.
  monthEstimate: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchManager ||
        role.isHeadOfficeAccounting ||
        role.isMaster,
    ),
  // เป้าหมายสายส่ง (รับจ่าย S6) — head office sets every route's daily sales target. Mirrors
  // ROUTE_SALES_TARGET_ROLES (head-office accounting); master passes the server's rbac.
  routeSalesTarget: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // รายงานรับจ่าย (รับจ่าย S1) — read-only profit (loss) by branch → route → day. Mirrors
  // INCOME_EXPENSE_REPORT_ROLES (branch accounting, branch finance, branch finance and accounting,
  // branch manager, head-office accounting); master passes the server's rbac as head office.
  incomeExpenseReport: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isBranchManager ||
        role.isHeadOfficeAccounting ||
        role.isMaster,
    ),
  // Head office and master name the branches (several); a branch role reads its token's branch.
  incomeExpenseReportAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  stockManagement: (role: UserRole) =>
    Boolean(role.isBranchManager || role.isMaster || role.isAdmin),
  // รถ — the vehicle list, add and edit. Mirrors the server's write cohort on /vehicles minus the
  // salesperson, who is field-only and adds a van from the PWA instead.
  vehicleManagement: (role: UserRole) =>
    Boolean(role.isBranchManager || role.isMaster || role.isAdmin),
  // Which of them may pick the branch. The server honours `branch_uuid` for exactly these two and
  // pins everyone else to the branch in their token.
  vehicleAllBranches: (role: UserRole) => Boolean(role.isMaster || role.isAdmin),
  // Route-plan upload — same audience as the stock upload: the branch manager
  // owns the plan for their own branch, admin/master may upload for any branch.
  pathwayUpload: (role: UserRole) =>
    Boolean(role.isBranchManager || role.isMaster || role.isAdmin),
  // สายส่ง (route management). Mirrors the server's GET /routes/management cohort: admin,
  // branch manager and head-office accounting; master passes every server gate. The branch
  // manager reads and manages only their own branch — the API pins it from the token.
  routeManagement: (role: UserRole) =>
    Boolean(
      role.isAdmin ||
        role.isMaster ||
        role.isBranchManager ||
        role.isHeadOfficeAccounting,
    ),
  // Which roles pick the branch on the route page: the server's all-branch cohort.
  routeAllBranches: (role: UserRole) =>
    Boolean(role.isAdmin || role.isMaster || role.isHeadOfficeAccounting),
  // Create / rename / delete a route. Mirrors POST / PATCH / DELETE /routes (admin, branch
  // manager; master passes). Head office only reads the page.
  routeWrite: (role: UserRole) =>
    Boolean(role.isAdmin || role.isMaster || role.isBranchManager),
  // Switching a route in or out of test mode changes its bill numbers and e-Tax sending, so
  // the server keeps `is_test_mode` to admin and master (restrictFieldsToRoles).
  routeTestModeEdit: (role: UserRole) => Boolean(role.isAdmin || role.isMaster),
  // Switching QR payment on a route changes how its customers pay, so the server keeps
  // `qr_payment_enabled` to the same admin / master cohort as test mode (restrictFieldsToRoles).
  routeQrEdit: (role: UserRole) => Boolean(role.isAdmin || role.isMaster),
  // PO โมเดิร์นเทรด, head-office side: นำเข้าไฟล์ PO (W2), รายการ PO ทุกสาขา (W1),
  // ส่งให้สาขา (W5), ระบุ/ย้ายสาขา and ดึงกลับ. Owner decision (round 2): head-office
  // accounting ONLY — `ho accounting` and `ho accounting supervisor` with equal rights
  // (both read `isHeadOfficeAccounting`), plus master, which the owner wants on every PO
  // screen. Admin stays out on purpose. The server's head-office cohort for these endpoints
  // is the same set; the two must move together or the menu opens onto a call that 403s.
  poManage: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // PO โมเดิร์นเทรด, branch side: PO ของสาขา (W7) — reading the branch's own POs,
  // จับคู่ร้านที่ค้าง, ส่งคืน HO and the Excel export, plus the drawer (W6) both sides share.
  // Mirrors the server's `purchaseOrderReadRoles` on GET /purchase-orders, GET /:uuid,
  // /export, /:uuid/customer-candidates and PATCH /:uuid/customer exactly: head-office
  // accounting across every branch, and at the branch the roles the handbook names for W7 —
  // ผู้จัดการสาขา and ธุรการสาขา. Branch finance alone is deliberately absent, as it is on
  // the server; admin was removed with poManage, master stays with it. Reaching another branch's PO is
  // refused per-row by the API, not by this key.
  poBranch: (role: UserRole) =>
    Boolean(
      role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinanceAndAccount ||
        role.isHeadOfficeAccounting ||
        role.isMaster,
    ),
  // ออเดอร์พิเศษ — the back office books an order for a customer, confirms it, hands it over
  // (mode B, at the branch) and closes route 99's day. The branch back office (accounting,
  // finance, finance-and-accounting, manager) for its own branch, head-office accounting for
  // any branch; no split between who drafts and who confirms. Admin and master act as
  // superusers, as everywhere else in this map; the server's back-office special-order
  // endpoints accept both.
  specialOrder: (role: UserRole) =>
    Boolean(
      role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount ||
        role.isBranchManager ||
        role.isHeadOfficeAccounting ||
        role.isAdmin ||
        role.isMaster,
    ),
  // Which special-order screens offer a branch picker. The tenant of an order is the
  // CUSTOMER's company, and only head office (either grade) and master may name one; a
  // branch user is pinned to the JWT branch by the server.
  specialOrderAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // Demand Factor — the multipliers the suggestion order applies to a store's demand.
  // Mirrors the server's rbac on /demand-adjustments: the branch manager (pinned to rows
  // that target its own branch only) and head-office accounting (every row); master passes
  // every server rbac check. ADMIN is deliberately absent, as the owner decided for the API.
  demandFactor: (role: UserRole) =>
    Boolean(role.isBranchManager || role.isHeadOfficeAccounting || role.isMaster),
  // Which Demand Factor screens offer a branch picker and multi-branch targets. Only head
  // office and master may name a branch; the server forces a branch manager's rows onto the
  // JWT branch.
  demandFactorAllBranches: (role: UserRole) =>
    Boolean(role.isHeadOfficeAccounting || role.isMaster),
  // Formula Scorer dashboard — analytics/oversight surface (mirrors the backend
  // ANALYTICS_ROLES: admin/master/HO-accounting/branch-manager).
  formulaScorer: (role: UserRole) =>
    Boolean(
      role.isAdmin ||
        role.isMaster ||
        role.isHeadOfficeAccounting ||
        role.isBranchManager,
    ),
  // Pivot — สรุปการขาย. Mirrors the backend spec's RBAC cohort (docs/openapi/
  // pivot.yaml): admin, head-office accounting, branch manager, branch accounting,
  // branch finance, branch finance and accounting. Field/sale roles never reach GDP.
  pivot: (role: UserRole) =>
    Boolean(
      role.isAdmin ||
        role.isMaster ||
        role.isHeadOfficeAccounting ||
        role.isBranchManager ||
        role.isBranchAccounting ||
        role.isBranchFinance ||
        role.isBranchFinanceAndAccount,
    ),
  // Product catalogue — the สินค้า menu and both of its screens (Products and
  // PriceBook). READING is open to every desktop role that already passed
  // canAccessGDP: branch staff look prices up daily, and the API keeps its GET
  // routes open to all roles too, so a client-side gate here would only hide
  // data the user can still fetch.
  // The one exception is a BDC-only user, who sees BDC and nothing else (the server keeps
  // them off the catalogue reads too, bar the product list the BDC SKU picker uses).
  product: (role: UserRole) => !role.isBdcOnly,
  // Maintaining the catalogue (create / edit / duplicate / delete, for products,
  // categories AND pricebooks, and editing product costs) is marketing's job. Mirrors the
  // backend's CATALOGUE_WRITE_ROLES: admin and marketing (master passes every check).
  productWrite: (role: UserRole) =>
    Boolean(role.isAdmin || role.isMaster || role.isMarketing),
  // ต้นทุนสินค้า — the catalogue writers, plus head-office accounting reading it without the
  // edit. Mirrors PRODUCT_COST_READ_ROLES on GET /product-costs: product costs are never
  // shown to a branch.
  productCostRead: isCatalogueReviewer,
  // ราคาตามช่วงเวลา (every book's price periods, its menu badge and the pricebook-list banner):
  // the same audience as the cost page. The API lets every role read the periods; this page is
  // marketing's work list, so a branch is not sent to it. Its writes need productWrite.
  pricePeriodRead: isCatalogueReviewer,
  // The shops on a pricebook come from every branch for the cross-branch roles and for
  // marketing, which maintains every branch's pricebooks; anyone else reads their own branch's.
  // Mirrors the store scope of the backend's pricebook reads.
  pricebookShopsAllBranches: (role: UserRole) =>
    Boolean(
      role.isAdmin ||
        role.isMaster ||
        role.isHeadOfficeAccounting ||
        role.isMarketing,
    ),
  // สาขา — the branch list and its readiness checklist. Mirrors the server's cohort on
  // GET /branches/overview and /branches/:uuid/readiness: master reads every branch, the
  // branch manager only their own (the server pins it to the token's branch).
  branchManagement: (role: UserRole) =>
    Boolean(role.isMaster || role.isBranchManager),
  // Creating and editing a branch and opening its routes (เปิดใช้) belong to master alone
  // (owner decision 2026-09-29); the branch manager reads the same screens.
  branchManagementWrite: (role: UserRole) => Boolean(role.isMaster),
  // BDC (7-Eleven DC) — นำเข้าไฟล์ EDI, คิวงาน, รายละเอียด shipment, ตั้งค่า. Mirrors the
  // server's BDC cohort: master + head-office accounting (either grade) + the two BDC roles
  // (หัวหน้า BDC, เจ้าหน้าที่ BDC). The generic branch roles are deliberately absent — BDC
  // branch users hold only the BDC roles. BDC is its own branch, so the server admits a BDC
  // user only when the token's branch_id is the `bdc.company_id` setting (403 otherwise) —
  // this key cannot know that id, it only keeps the menu off roles that can never pass.
  // The BDC roles appear in NO other key of this map.
  bdc: (role: UserRole) =>
    Boolean(
      role.isMaster ||
        role.isHeadOfficeAccounting ||
        role.isBdcManager ||
        role.isBdcOperator,
    ),
  // Editing the BDC masters (DC name, SKU pcs_per_case / pcs_per_tray / GB product link):
  // master and หัวหน้า BDC (owner 2026-10-27). เจ้าหน้าที่ BDC and head office see the settings
  // tabs read-only.
  bdcWrite: (role: UserRole) => Boolean(role.isMaster || role.isBdcManager),
  // Registering a GDP branch as a BDC branch and its test mode (PUT /bdc/branches): master
  // alone, as on the server.
  bdcBranchWrite: (role: UserRole) => Boolean(role.isMaster),
  // ขาด-เกิน (stock day) — everyone with `bdc` reads it (head office, เจ้าหน้าที่ BDC); typing
  // the GM's figures and ปิดวัน belong to หัวหน้า BDC (the GM) and master, as on the server.
  bdcStockDayWrite: (role: UserRole) => Boolean(role.isMaster || role.isBdcManager),
  // Reopening a closed day: master alone.
  bdcStockDayReopen: (role: UserRole) => Boolean(role.isMaster),
  // ใบจ่ายสินค้า — print and บันทึกใบจ่ายกลับ: master and both BDC roles (the desk prints and
  // types back the driver's sheet), as on the server. Head office reads it.
  bdcDispatchWrite: (role: UserRole) =>
    Boolean(role.isMaster || role.isBdcManager || role.isBdcOperator),
  // นำเข้าไฟล์, ยอดส่ง, ยืนยัน DO, ออก / ยกเลิก INV and สร้างไฟล์ส่ง 7-11: the BDC team and
  // master only, as on the server (which still keeps an operator on its own branch). Only the
  // BDC team runs the 7-11 EAI app, so head office reads these and prints (ศูนย์เอกสาร) but
  // never changes them (owner 2026-10-27).
  bdcDesk: (role: UserRole) =>
    Boolean(role.isMaster || role.isBdcManager || role.isBdcOperator),
  // Set / Reset of the INV running numbers (POST /bdc/invoice-sequence/set|reset): master only
  // (owner decision 2026-10-19); everyone with `bdc` reads the next numbers and their history.
  bdcInvoiceSequenceWrite: (role: UserRole) => Boolean(role.isMaster),
  // CN ใบลดหนี้ — everyone with `bdc` reads; create / edit a draft, ออก CN, ยกเลิก and the RETSEV
  // file are the BDC team's and master's, like the INV, as on the server.
  bdcCreditNoteWrite: (role: UserRole) =>
    Boolean(role.isMaster || role.isBdcManager || role.isBdcOperator),
  // ใบวางบิล (GET/POST /bdc/billings…) — head-office accounting (either grade) and master only;
  // the BDC roles neither see the menu nor reach the page.
  bdcBilling: (role: UserRole) => Boolean(role.isMaster || role.isHeadOfficeAccounting),
} as const;

export type FeatureKey = keyof typeof featureAccess;
